/**
 * MUTAGIM.SHOP — Admin Brands Marquee Page
 * Gestion du bandeau de marques défilant
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Loader2,
  Save,
  X,
  Image as ImageIcon,
  Sparkles,
  ExternalLink,
  Settings2,
  Eye,
  Mic
} from 'lucide-react';
import { VoiceInput } from '../components/ui/VoiceInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminBrandsMarqueePage() {
  const { isLight } = useAdminTheme();
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState({
    is_enabled: true,
    speed: 30,
    background_color: '#000000',
    title: { fr: '', en: '', he: '' }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [generatingLogo, setGeneratingLogo] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    link_url: '',
    is_active: true
  });

  const token = localStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/brands-marquee/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch brands');
      
      const data = await res.json();
      setBrands(data.brands || []);
      if (data.settings) {
        setSettings({
          is_enabled: data.settings.is_enabled ?? true,
          speed: data.settings.speed || 30,
          background_color: data.settings.background_color || '#000000',
          title: data.settings.title || { fr: '', en: '', he: '' }
        });
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenCreate = () => {
    setEditingBrand(null);
    setFormData({
      name: '',
      logo_url: '',
      link_url: '',
      is_active: true
    });
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      logo_url: brand.logo_url || '',
      link_url: brand.link_url || '',
      is_active: brand.is_active ?? true
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!formData.name) {
        toast.error('Le nom de la marque est requis');
        return;
      }

      const endpoint = editingBrand 
        ? `${API_URL}/api/brands-marquee/admin/${editingBrand.id}`
        : `${API_URL}/api/brands-marquee/admin`;
      
      const method = editingBrand ? 'PUT' : 'POST';
      
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save brand');
      }
      
      toast.success(editingBrand ? 'Marque mise à jour' : 'Marque ajoutée');
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!brandToDelete) return;
    
    try {
      setSaving(true);
      
      const res = await fetch(`${API_URL}/api/brands-marquee/admin/${brandToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Marque supprimée');
      setDeleteDialogOpen(false);
      setBrandToDelete(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      const res = await fetch(`${API_URL}/api/brands-marquee/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) throw new Error('Failed to save settings');
      
      toast.success('Paramètres enregistrés');
      setSettingsDialogOpen(false);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (brand) => {
    try {
      await fetch(`${API_URL}/api/brands-marquee/admin/${brand.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !brand.is_active })
      });
      
      toast.success(brand.is_active ? 'Marque désactivée' : 'Marque activée');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleGenerateLogo = async () => {
    if (!formData.name) {
      toast.error('Entrez d\'abord le nom de la marque');
      return;
    }
    
    try {
      setGeneratingLogo(true);
      toast.info('Génération du logo en cours...', { duration: 15000, id: 'gen-logo' });
      
      const res = await fetch(`${API_URL}/api/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: `Create a professional brand logo for "${formData.name}". The logo should be a clean, modern icon or symbol representing the brand identity. Style: minimalist vector-style design with bold shapes, high contrast. The logo must be clearly visible with distinct shapes. Use a simple dark/black background with a white or light-colored logo design. NO text, just the iconic symbol/emblem.`,
          size: "1024x1024"
        })
      });
      
      if (!res.ok) throw new Error('Failed to generate logo');
      
      const result = await res.json();
      
      if (result.image_url) {
        setFormData(prev => ({ ...prev, logo_url: result.image_url }));
        toast.dismiss('gen-logo');
        toast.success('Logo généré avec succès !');
      }
    } catch (err) {
      toast.dismiss('gen-logo');
      toast.error('Erreur lors de la génération du logo');
    } finally {
      setGeneratingLogo(false);
    }
  };

  return (
    <AdminPageWrapper 
      title="Bandeau Marques" 
      subtitle="Gérer le carrousel de logos des marques partenaires"
    >
      <div className="space-y-6" data-testid="admin-brands-marquee-page">
        {/* Header Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => {
                  setSettings(prev => ({ ...prev, is_enabled: checked }));
                  // Auto-save
                  fetch(`${API_URL}/api/brands-marquee/admin/settings`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ ...settings, is_enabled: checked })
                  });
                }}
              />
              <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                {settings.is_enabled ? 'Actif' : 'Désactivé'}
              </span>
            </div>
            <span className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-500")}>
              {brands.length} marque{brands.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setSettingsDialogOpen(true)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/', '_blank')}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              <Eye className="w-4 h-4 mr-2" />
              Prévisualiser
            </Button>
            <Button
              onClick={handleOpenCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-brand-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une marque
            </Button>
          </div>
        </div>

        {/* Brands Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : brands.length === 0 ? (
          <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ImageIcon className={cn("w-12 h-12 mb-4", isLight ? "text-gray-300" : "text-gray-600")} />
              <p className={cn("text-lg font-medium mb-2", isLight ? "text-gray-700" : "text-gray-300")}>
                Aucune marque
              </p>
              <p className={cn("text-sm mb-4 text-center", isLight ? "text-gray-500" : "text-gray-500")}>
                Ajoutez des marques pour créer un bandeau défilant sur votre page d'accueil
              </p>
              <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une marque
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {brands.map((brand) => (
              <Card 
                key={brand.id}
                className={cn(
                  "group relative overflow-hidden transition-all",
                  !brand.is_active && "opacity-50",
                  isLight ? "bg-white border-gray-200 hover:border-gray-300" : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-700"
                )}
              >
                <CardContent className="p-4">
                  <div className="aspect-square mb-3 rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    {brand.logo_url && brand.logo_url !== 'None' ? (
                      <img 
                        src={brand.logo_url.startsWith('/api') ? `${API_URL}${brand.logo_url}` : brand.logo_url}
                        alt={brand.name}
                        className="w-full h-full object-contain p-4"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-white/30">
                        {brand.name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  <p className={cn(
                    "font-medium text-sm truncate mb-2",
                    isLight ? "text-gray-900" : "text-white"
                  )}>
                    {brand.name}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <Switch
                      checked={brand.is_active}
                      onCheckedChange={() => handleToggleActive(brand)}
                    />
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenEdit(brand)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:text-red-400"
                        onClick={() => { setBrandToDelete(brand); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              {editingBrand ? 'Modifier la marque' : 'Ajouter une marque'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Nom de la marque *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Nike, Adidas..."
                className={cn(
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>

            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Logo (URL)</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://..."
                  className={cn(
                    "flex-1",
                    isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                />
                <Button
                  variant="outline"
                  onClick={handleGenerateLogo}
                  disabled={generatingLogo || !formData.name}
                  className={cn(
                    isLight ? "border-purple-300 text-purple-700" : "border-purple-500/50 text-purple-400"
                  )}
                >
                  {generatingLogo ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {formData.logo_url && (
                <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden bg-gray-900 p-2">
                  <img 
                    src={formData.logo_url}
                    alt="Preview"
                    className="w-full h-full object-contain filter brightness-0 invert"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Lien (optionnel)</Label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                placeholder="https://..."
                className={cn(
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Marque active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingBrand ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Paramètres du bandeau
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                Vitesse de défilement: {settings.speed}s
              </Label>
              <Slider
                value={[settings.speed]}
                onValueChange={([value]) => setSettings(prev => ({ ...prev, speed: value }))}
                min={10}
                max={60}
                step={5}
              />
              <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                Durée pour un cycle complet
              </p>
            </div>

            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Couleur de fond</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.background_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input
                  value={settings.background_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                  className={cn(
                    "flex-1",
                    isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Titre (FR)</Label>
              <Input
                value={settings.title?.fr || ''}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  title: { ...prev.title, fr: e.target.value }
                }))}
                placeholder="Nos Marques Partenaires"
                className={cn(
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Supprimer la marque ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{brandToDelete?.name}" ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPageWrapper>
  );
}

export default AdminBrandsMarqueePage;
